"use client";

import type { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";

import { useIsTouchDevice } from "./use-is-touch-device";

type PositionerProps = PopoverPrimitive.Positioner.Props;
export type AnchoredSide = NonNullable<PositionerProps["side"]>;
export type AnchoredCollisionAvoidance = NonNullable<
  PositionerProps["collisionAvoidance"]
>;
type Anchor = PositionerProps["anchor"];

/** Breathing room kept between the unit and the keyboard's top edge, and
 *  between the unit and the visible top when the unit is taller than the
 *  room left. */
export const KEYBOARD_REVEAL_MARGIN = 16;

/** While a field inside the popup holds the keyboard, the popup keeps the
 *  side it had: flipping to the other side of its anchor is what detaches it
 *  from the cell it edits. Alignment still shifts, which only slides it
 *  along the anchor. */
const HOLD_SIDE: AnchoredCollisionAvoidance = { align: "shift", side: "none" };

interface Edges {
  top: number;
  bottom: number;
}

/**
 * How far to scroll the anchor's container so the unit — anchor plus popup,
 * as they stood when the field took focus — sits above the keyboard, with
 * `margin` of air. Zero when it already fits. Capped so the unit's top never
 * leaves the visible region: a unit taller than the room left keeps its top
 * and lets its tail sit under the keyboard, which the popup's own scrolling
 * body then reaches.
 */
export function keyboardRevealDelta({
  anchor,
  popup,
  visibleTop,
  visibleBottom,
  margin = KEYBOARD_REVEAL_MARGIN,
}: {
  anchor: Edges;
  popup: Edges;
  visibleTop: number;
  visibleBottom: number;
  margin?: number;
}): number {
  const unitTop = Math.min(anchor.top, popup.top);
  const unitBottom = Math.max(anchor.bottom, popup.bottom);
  const overlap = unitBottom + margin - visibleBottom;
  if (overlap <= 0) return 0;
  const room = unitTop - (visibleTop + margin);
  return Math.max(0, Math.min(overlap, room));
}

/** Whether `target` is a field the software keyboard serves: a writable
 *  text-like input, a textarea, or editable content. */
export function isEditable(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return !target.readOnly;
  if (target instanceof HTMLInputElement) {
    if (target.readOnly) return false;
    switch (target.type) {
      case "button":
      case "checkbox":
      case "color":
      case "file":
      case "hidden":
      case "image":
      case "radio":
      case "range":
      case "reset":
      case "submit":
        return false;
      default:
        return true;
    }
  }
  return target.isContentEditable;
}

function isScrollable(element: Element): boolean {
  const { overflowY } = getComputedStyle(element);
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay")
    return false;
  return element.scrollHeight > element.clientHeight + 1;
}

/**
 * Scrolls `delta` px down through the anchor's scroll ancestors, innermost
 * first — a table body inside a page pane hands the remainder outward once
 * it reaches its own end. The window takes whatever is left — unless the
 * anchor stands in a fixed layer (a drawer, a dialog): scrolling the window
 * cannot move a fixed layer's content, and on iOS with the keyboard up it
 * shoves the whole layer off the screen instead. That remainder is dropped.
 */
function scrollAncestorsBy(anchor: Element, delta: number): number {
  let remaining = delta;
  let fixedLayer = false;
  let node: Element | null = anchor.parentElement;
  while (node && remaining > 0.5) {
    if (isScrollable(node)) {
      const before = node.scrollTop;
      node.scrollTop = before + remaining;
      remaining -= node.scrollTop - before;
    }
    if (!fixedLayer && getComputedStyle(node).position === "fixed") {
      fixedLayer = true;
    }
    node = node.parentElement;
  }
  if (remaining > 0.5 && !fixedLayer) {
    const before = window.scrollY;
    window.scrollBy(0, remaining);
    remaining -= window.scrollY - before;
  }
  return delta - remaining;
}

function resolveAnchorElement(
  anchor: Anchor,
  popup: HTMLElement,
): Element | null {
  let candidate: unknown = anchor;
  if (typeof candidate === "function") candidate = candidate();
  if (candidate && typeof candidate === "object" && "current" in candidate) {
    candidate = candidate.current;
  }
  if (candidate instanceof Element) return candidate;
  if (
    candidate &&
    typeof candidate === "object" &&
    "getBoundingClientRect" in candidate &&
    typeof candidate.getBoundingClientRect === "function"
  ) {
    // A virtual anchor has a rect and no node: the node under its centre is
    // the thing it stands for, and that node's ancestors are what scroll.
    const rect = (candidate.getBoundingClientRect as () => DOMRect)();
    const hit = popup.ownerDocument.elementFromPoint(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
    );
    return hit && !popup.contains(hit) ? hit : null;
  }
  // No anchor of its own: the popup hangs off its trigger, which Base UI
  // points at the popup's id.
  if (popup.id) {
    return popup.ownerDocument.querySelector(
      `[aria-controls="${CSS.escape(popup.id)}"]`,
    );
  }
  return null;
}

interface Held {
  /** Whether the side is still held. Released once a scroll could not
   *  reveal the unit — an anchor in a fixed layer with no room to scroll (a
   *  dialog on a tablet): holding the side would leave the popup under the
   *  keyboard, so it is handed back to Base UI, which flips it above the
   *  anchor into the room the keyboard left. */
  hold: boolean;
  side: AnchoredSide | undefined;
  /** The popup's edges relative to the anchor's top when the field took
   *  focus — the popup rides its anchor, so this stays true as the anchor
   *  scrolls, and it survives the squeeze the keyboard puts on the popup's
   *  height before the anchor has moved. */
  popupTopOffset: number;
  popupHeight: number;
}

/**
 * An anchored popup that a soft keyboard would push off its anchor stays
 * with it: the row scrolls up and the popup rides along, so the cell being
 * edited and its editor stay one picture (Linear's behaviour on an iPad).
 *
 * Two things go wrong without this. floating-ui reads the visual viewport
 * as its boundary, so the keyboard's rise flips a bottom-anchored popup to
 * the top of its cell or shifts it away; and the browser reveals only the
 * focused field — the popup lives in a portal, so the cell's own scroll
 * container never moves and the anchor ends up under the keyboard while
 * the popup floats free.
 *
 * While an editable inside the popup holds focus on a coarse pointer, the
 * positioner keeps the side it had (returned `side` and
 * `collisionAvoidance`), and each visual-viewport change scrolls the
 * anchor's nearest scrollable ancestors so anchor + popup clear the
 * keyboard, `keyboardRevealDelta` deciding by how much. Base UI's anchor
 * tracking then moves the popup with its anchor. Nothing scrolls back when
 * the keyboard closes. Inert on a fine pointer.
 */
export function useKeyboardAnchorFollow({
  popup,
  anchor,
}: {
  /** The mounted popup element, or null while closed. */
  popup: HTMLElement | null;
  anchor: Anchor;
}): {
  side: AnchoredSide | undefined;
  collisionAvoidance: AnchoredCollisionAvoidance | undefined;
} {
  const touch = useIsTouchDevice();
  const [held, setHeld] = React.useState<Held | null>(null);
  const anchorRef = React.useRef(anchor);
  anchorRef.current = anchor;

  React.useEffect(() => {
    if (!touch || !popup) return;
    const positioner = popup.parentElement;
    let current: Held | null = null;

    const reveal = (): void => {
      if (!current) return;
      const anchorEl = resolveAnchorElement(anchorRef.current, popup);
      if (!anchorEl) return;
      const anchorRect = anchorEl.getBoundingClientRect();
      const viewport = window.visualViewport;
      const visibleTop = viewport ? viewport.offsetTop : 0;
      const visibleBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      const popupTop = anchorRect.top + current.popupTopOffset;
      const delta = keyboardRevealDelta({
        anchor: { bottom: anchorRect.bottom, top: anchorRect.top },
        popup: { bottom: popupTop + current.popupHeight, top: popupTop },
        visibleBottom,
        visibleTop,
      });
      if (delta <= 0) return;
      const scrolled = scrollAncestorsBy(anchorEl, delta);
      if (current.hold && delta - scrolled > 0.5) {
        current = { ...current, hold: false };
        setHeld(current);
      }
    };

    const listen = (): void => {
      window.visualViewport?.addEventListener("resize", reveal);
      window.visualViewport?.addEventListener("scroll", reveal);
      window.addEventListener("resize", reveal);
    };
    const unlisten = (): void => {
      window.visualViewport?.removeEventListener("resize", reveal);
      window.visualViewport?.removeEventListener("scroll", reveal);
      window.removeEventListener("resize", reveal);
    };

    const onFocusIn = (event: FocusEvent): void => {
      if (!isEditable(event.target)) return;
      if (current) return;
      const anchorEl = resolveAnchorElement(anchorRef.current, popup);
      const anchorTop = anchorEl?.getBoundingClientRect().top ?? 0;
      const rect = (positioner ?? popup).getBoundingClientRect();
      const side = positioner?.dataset.side as AnchoredSide | undefined;
      current = {
        hold: true,
        popupHeight: rect.height,
        popupTopOffset: rect.top - anchorTop,
        side,
      };
      setHeld(current);
      listen();
      reveal();
    };
    const onFocusOut = (event: FocusEvent): void => {
      if (!current) return;
      const next = event.relatedTarget;
      if (next instanceof Node && popup.contains(next) && isEditable(next))
        return;
      current = null;
      setHeld(null);
      unlisten();
    };

    popup.addEventListener("focusin", onFocusIn);
    popup.addEventListener("focusout", onFocusOut);
    return () => {
      popup.removeEventListener("focusin", onFocusIn);
      popup.removeEventListener("focusout", onFocusOut);
      unlisten();
      setHeld(null);
    };
  }, [popup, touch]);

  return {
    collisionAvoidance: held?.hold ? HOLD_SIDE : undefined,
    side: held?.hold ? held.side : undefined,
  };
}
